import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { transformUsageHistory } from '../../../utils/analyticsUtils';
import { useNavigate } from 'react-router-dom';
import { User, Activity, Rocket, MessageSquare, BarChart2, Coins } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { cn } from '../../../lib/utils';
import { fetchCreditBalance } from '../../../apiLayer/dashBoardAPIs/vsCodeDashboardAPIs';

const Overview = ({ userInfo, subscriptions, usageHistory, fetchPeriodUsage, onTabChange, paymentInfo }) => {
  const navigate = useNavigate();

  const [timeFilter, setTimeFilter] = useState('month');
  const [totalUsage, setTotalUsage] = useState(0);
  const [periodUsage, setPeriodUsage] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [creditBalance, setCreditBalance] = useState(null);

  const isUltraUser = userInfo?.users_plan_name === 'ultra';
  // Lifetime owners have no subscription — users_plan_name (from
  // /payment/get_user_info, merged in the dashboard hook) is the source of truth.
  const isLifetimeUser = userInfo?.users_plan_name === 'lifetime';

  useEffect(() => {
    if (!isUltraUser) return;
    const getCredits = async () => {
      const result = await fetchCreditBalance();
      setCreditBalance(result?.credit_balance ?? 0);
    };
    getCredits();
  }, [isUltraUser]);

  useEffect(() => {
    const getUsageData = async () => {
      const total = await fetchPeriodUsage('total');
      setTotalUsage(total);

      const interval = subscriptions?.active_subscription?.plans?.[0]?.interval;
      if (interval === 'month') {
        const monthlyUsage = await fetchPeriodUsage('month');
        setPeriodUsage(monthlyUsage);
      } else if (interval === 'year') {
        const yearlyUsage = await fetchPeriodUsage('year');
        setPeriodUsage(yearlyUsage);
      }
    };
    getUsageData();
  }, [fetchPeriodUsage, subscriptions]);

  useEffect(() => {
    if (usageHistory) {
      const data = transformUsageHistory(usageHistory, timeFilter);
      setChartData(data);
    }
  }, [usageHistory, timeFilter]);

  const getPlanName = () => {
    if (isLifetimeUser) return 'Lifetime';
    if (subscriptions?.is_subscriber && subscriptions?.plans?.length > 0) {
      return subscriptions.plans[0];
    }
    return 'Free Plan';
  };

  const isUnlimited = () => {
    const planName = subscriptions?.active_subscription?.plans?.[0]?.plan_name;
    return subscriptions?.is_subscriber && planName?.toLowerCase().includes('unlimited');
  };

  const getPeriodUsageDisplay = () => {
    const interval = subscriptions?.active_subscription?.plans?.[0]?.interval;
    if (interval === 'month' || interval === 'year') {
      return isUnlimited() ? 'Unlimited' : `${periodUsage}`;
    }
    return isUnlimited() ? 'Unlimited' : `${totalUsage}`;
  };

  const getPeriodLabel = () => {
    const interval = subscriptions?.active_subscription?.plans?.[0]?.interval;
    if (interval === 'month') return 'This Month Usage';
    if (interval === 'year') return 'This Year Usage';
    return 'Token Usage';
  };

  const getPeriodDescription = () => {
    const interval = subscriptions?.active_subscription?.plans?.[0]?.interval;
    if (interval === 'month') return `${periodUsage} tokens used this month.`;
    if (interval === 'year') return `${periodUsage} tokens used this year.`;
    if (isUnlimited()) return 'You have unlimited token usage with your current plan.';
    return `${totalUsage} tokens used.`;
  };

  return (
    <div className="flex-1 bg-[#0a0a0f] p-8 overflow-y-auto">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-white/5 border border-white/10">
            <User className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">
              Hi, <span className="text-primary">{userInfo?.fullname || 'User'}</span>
            </h1>
            <p className="text-sm text-muted-foreground">Welcome back to your dashboard</p>
          </div>
        </div>
      </div>

      {!subscriptions?.is_subscriber && !isLifetimeUser ? (
        <Card className="mb-8 border-primary/20 bg-gradient-to-r from-primary/10 to-purple-600/10">
          <CardContent className="flex items-center justify-between p-6">
            <div>
              <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-primary" /> Unlock Premium Features
              </h3>
              <p className="text-muted-foreground">Upgrade to a paid plan to access unlimited tokens, advanced AI models, and priority support.</p>
            </div>
            <Button onClick={() => navigate('/pricing?referrer=vscode')} className="ml-4">
              Upgrade Now
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-8 border-white/10 bg-white/5">
          <CardContent className="flex items-center justify-between p-6">
            <div>
              <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-white" /> Need Help?
              </h3>
              <p className="text-muted-foreground">Having issues? Our support team is here to help you resolve any problems quickly.</p>
            </div>
            <Button variant="outline" onClick={() => onTabChange && onTabChange('contact')} className="ml-4">
              Contact Support
            </Button>
          </CardContent>
        </Card>
      )}

      {isUltraUser && (
        <Card className="mb-8 border-primary/20 bg-[#121219]/80">
          <CardContent className="flex items-center justify-between p-6">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                <Coins className="w-4 h-4 text-primary" /> Credit Balance
              </h3>
              <div className="text-3xl font-bold text-white">
                {creditBalance === null ? '—' : `$${creditBalance.toFixed(2)}`}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Extra credits are used automatically when your included Ultra budget is spent.
              </p>
            </div>
            <Button onClick={() => navigate('/buy-credits')} className="ml-4">
              Add Credits
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {getPeriodLabel()}
            </CardTitle>
            <Activity className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white mb-1">{getPeriodUsageDisplay()}</div>
            <p className="text-xs text-muted-foreground">Current Plan: <span className="text-primary">{getPlanName()}</span></p>
            <p className="text-xs text-muted-foreground mt-2">{getPeriodDescription()}</p>
          </CardContent>
        </Card>

        <Card className="bg-[#121219]/80 border-white/10">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Usage
            </CardTitle>
            <BarChart2 className="w-4 h-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white mb-1">{totalUsage}</div>
            <p className="text-xs text-muted-foreground">Lifetime tokens consumed</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-2 mb-6 bg-white/5 w-fit p-1 rounded-lg border border-white/10">
        {['week', 'month', 'year'].map((filter) => (
          <button
            key={filter}
            onClick={() => setTimeFilter(filter)}
            className={cn(
              "px-4 py-1.5 rounded-md text-sm font-medium transition-all",
              timeFilter === filter 
                ? "bg-primary text-white shadow-sm" 
                : "text-muted-foreground hover:text-white hover:bg-white/5"
            )}
          >
            {filter === 'week' ? 'Last 7 Days' : filter === 'month' ? 'This Month' : 'This Year'}
          </button>
        ))}
      </div>

      <Card className="bg-[#121219]/80 border-white/10">
        <CardHeader>
          <CardTitle>Token Usage Trends</CardTitle>
          <CardDescription>Visualizing your AI consumption over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                <XAxis 
                  dataKey="key" 
                  stroke="#666" 
                  fontSize={12} 
                  tickLine={false}
                  axisLine={false}
                  dy={10}
                />
                <YAxis 
                  stroke="#666" 
                  fontSize={12} 
                  tickLine={false}
                  axisLine={false}
                  dx={-10}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1a1a24', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  itemStyle={{ color: '#fff' }}
                  labelStyle={{ color: '#a0a0a0' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#E005FF" 
                  strokeWidth={3}
                  dot={{ fill: '#E005FF', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#E005FF', strokeWidth: 2, fill: '#fff' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Overview;
